/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useEffect, useState } from 'react';
import BackButton from '../common/BackButton';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Component, ParticipantType, Bid, ComponentType } from '../../types';
import './CreateBid.css';

interface CollectionItemData {
  id: number;
  componentId: number;
  componentName: string;
  manufacturerCode: string;
  actualCount: number;
}

interface BidData {
  id: number;
  name: string;
  internalNotes: string;
  price: number;
  filePath: string;
  dueDate: string;
  coverSheet: string;
  archiveUntilDate: string;
  authorUsername: string;
  archivedFlag?: boolean;
  bidGroupId?: number;
  versionNumber?: number;
  pdfGenerateDate?: string;
  bidBodies?: Array<{
    id: number;
    pdfId: number; // API returns pdfId for the component reference
    componentType?: string;
    componentName?: string;
    notes: string;
    identificationCode: string;
    sequenceNumber: number;
    count: number;
    price: number;
    collectionItems?: CollectionItemData[];
  }>;
}

// Interface for expanded component summary (individual items from collections)
interface ExpandedComponentSummary {
  component: Component;
  totalCount: number;
}

const ViewBid: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [bid, setBid] = useState<BidData | null>(null);
  const [components, setComponents] = useState<Component[]>([]);
  const [expandedSummary, setExpandedSummary] = useState<ExpandedComponentSummary[]>([]);
  const [versions, setVersions] = useState<Bid[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadBidData(parseInt(id));
    }
  }, [id]);

  const loadBidData = async (bidId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use appropriate endpoint based on role
      let bidResponse;
      if (currentUser?.type === ParticipantType.ADMIN) {
        bidResponse = await apiClient.getAdminBidById(bidId);
      } else if (currentUser?.type === ParticipantType.MANAGER) {
        bidResponse = await apiClient.getManagerBidById(bidId);
      } else {
        bidResponse = await apiClient.getBidById(bidId);
      }

      if (!bidResponse.success || !bidResponse.bid) {
        setError('Failed to load bid data');
        return;
      }

      const bidData = bidResponse.bid as any;
      setBid(bidData);

      // Fetch PDF details for each bid body
      if (bidData.bidBodies && Array.isArray(bidData.bidBodies)) {
        // Filter out bodies with invalid componentIds (API uses pdfId)
        const validBodies = bidData.bidBodies.filter((body: any) => body.pdfId != null && body.pdfId !== 0);
        const componentPromises = validBodies.map((body: any) =>
          apiClient.getComponentById(body.pdfId)
        );
        const componentResponses = await Promise.all(componentPromises);

        const loadedComponents: Component[] = [];
        componentResponses.forEach((response) => {
          if (response.success && response.component) {
            loadedComponents.push(response.component);
          }
        });

        setComponents(loadedComponents);

        // Build expanded component summary (expand collections to individual items)
        // Uses actual_count from BidBodyCollectionItems when available
        const aggregated = new Map<number, { component: Component; totalCount: number }>();

        for (let i = 0; i < validBodies.length; i++) {
          const bidBody = validBodies[i];
          const component = loadedComponents[i];
          if (!component) continue;

          const bidBodyCount = bidBody.count || 1;

          if (component.type === ComponentType.COLLECTION) {
            // Check if we have collectionItems with actualCount
            if (bidBody.collectionItems && bidBody.collectionItems.length > 0) {
              // Use actual counts from BidBodyCollectionItems
              for (const collItem of bidBody.collectionItems) {
                const itemCount = (collItem.actualCount || 1) * bidBodyCount;

                if (aggregated.has(collItem.componentId)) {
                  aggregated.get(collItem.componentId)!.totalCount += itemCount;
                } else {
                  // Fetch component details for display
                  try {
                    const componentResponse = await apiClient.getComponentById(collItem.componentId);
                    if (componentResponse.success && componentResponse.component) {
                      aggregated.set(collItem.componentId, {
                        component: componentResponse.component,
                        totalCount: itemCount
                      });
                    }
                  } catch (err) {
                    console.error('Error fetching component for collection item:', err);
                  }
                }
              }
            } else {
              // Fallback to fetching collection items with suggested counts
              try {
                const itemsResponse = await apiClient.getCollectionItems(component.id);
                if (itemsResponse.success && itemsResponse.items) {
                  for (const item of itemsResponse.items) {
                    const itemComponent = item.component;
                    const itemCount = (item.suggestedCount || 1) * bidBodyCount;

                    if (aggregated.has(itemComponent.id)) {
                      aggregated.get(itemComponent.id)!.totalCount += itemCount;
                    } else {
                      aggregated.set(itemComponent.id, { component: itemComponent, totalCount: itemCount });
                    }
                  }
                }
              } catch (err) {
                console.error('Error fetching collection items:', err);
              }
            }
          } else {
            // Regular ITEM - add directly
            if (aggregated.has(component.id)) {
              aggregated.get(component.id)!.totalCount += bidBodyCount;
            } else {
              aggregated.set(component.id, { component, totalCount: bidBodyCount });
            }
          }
        }

        setExpandedSummary(Array.from(aggregated.values()));
      }

      // Load version history
      loadVersionHistory(bidId);
    } catch (err: any) {
      console.error('Error loading bid data:', err);
      setError(err.message || 'Failed to load bid data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadVersionHistory = async (bidId: number) => {
    try {
      const response = await apiClient.getBidVersions(bidId);
      if (response.success && response.versions) {
        setVersions(response.versions);
      }
    } catch (err: any) {
      console.error('Error loading version history:', err);
      // Don't show error to user - version history is optional
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="create-bid-container">
      <BackButton to="/bids" />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading bid details...</p>
        </div>
      </div>
    );
  }

  if (error || !bid) {
    return (
      <div className="create-bid-container">
        <div className="error-message">
          {error || 'Bid not found'}
        </div>
        <button
          type="button"
          onClick={handleBack}
          className="cancel-button"
          style={{ marginTop: '1rem' }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="create-bid-container">
      <BackButton to="/bids" />
      <div className="create-bid-header">
        <h2>View Bid: {bid.name}</h2>
      </div>

      <div className="create-bid-form">
        <div className="form-group">
          <label>Bid Name</label>
          <input
            type="text"
            value={bid.name}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label>Author</label>
          <input
            type="text"
            value={bid.authorUsername}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label>File Path</label>
          <input
            type="text"
            value={bid.filePath}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label>Total Price</label>
          <input
            type="text"
            value={`$${bid.price?.toFixed(2) || '0.00'}`}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label>Due Date</label>
          <input
            type="text"
            value={formatDate(bid.dueDate)}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label>Archive Until Date</label>
          <input
            type="text"
            value={formatDate(bid.archiveUntilDate)}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
        </div>

        {/* Aggregated Component Summary - Shows individual items (collections are expanded) */}
        {expandedSummary.length > 0 && (
          <div className="form-group">
            <label>Component Summary</label>
            <div style={{
              padding: '1rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              backgroundColor: '#f0f9ff'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#374151' }}>Manufacturer Code</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem', color: '#374151' }}>Component Name</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem', color: '#374151' }}>Total Count</th>
                  </tr>
                </thead>
                <tbody>
                  {expandedSummary.map(({ component, totalCount }) => (
                    <tr key={component.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace', color: '#1f2937' }}>
                        {component.manufacturerCode || 'N/A'}
                      </td>
                      <td style={{ padding: '0.5rem', color: '#4b5563' }}>
                        {component.name}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#1f2937' }}>
                        {totalCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Included Components</label>
          {bid.bidBodies && bid.bidBodies.length > 0 ? (
            <div className="pdf-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {bid.bidBodies.map((bidBody, index) => {
                const component = components[index];
                return (
                  <div
                    key={bidBody.id}
                    style={{
                      padding: '1rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      backgroundColor: '#f9fafb'
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                      #{bidBody.sequenceNumber} - {component?.name || 'Loading...'}
                    </div>
                    {component && (
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                        {component.manufacturerName || component.manufacturerCode ? (
                          <>
                            {component.manufacturerName || 'N/A'} | {component.manufacturerCode || 'N/A'}
                          </>
                        ) : (
                          <span style={{ fontStyle: 'italic' }}>No manufacturer info</span>
                        )}
                        {component.price && ` | Component Price: $${component.price.toFixed(2)}`}
                      </div>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      {bidBody.count > 1 && <div><strong>Count:</strong> {bidBody.count}</div>}
                      {bidBody.price && <div><strong>Bid Body Price:</strong> ${bidBody.price.toFixed(2)}</div>}
                      {bidBody.identificationCode && <div><strong>ID Code:</strong> {bidBody.identificationCode}</div>}
                      {bidBody.notes && <div><strong>Notes:</strong> {bidBody.notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontSize: '0.875rem' }}>
              No components included in this bid.
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Cover Sheet</label>
          {bid.coverSheet ? (
            <div
              style={{
                padding: '1rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#f9fafb',
                minHeight: '200px',
                maxHeight: '400px',
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: bid.coverSheet }}
            />
          ) : (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
              No cover sheet content
            </div>
          )}
        </div>

        {versions.length > 1 && (
          <div className="form-group">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Version History ({versions.length} versions)</span>
              <button
                type="button"
                onClick={() => setShowVersions(!showVersions)}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {showVersions ? 'Hide Versions' : 'Show Versions'}
              </button>
            </label>
            {showVersions && (
              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {versions.map((version) => (
                  <div
                    key={version.id}
                    style={{
                      padding: '1rem',
                      border: version.archivedFlag ? '1px solid #e5e7eb' : '2px solid #3b82f6',
                      borderRadius: '6px',
                      backgroundColor: version.archivedFlag ? '#f9fafb' : '#eff6ff'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                          Version {version.versionNumber}
                          {!version.archivedFlag && (
                            <span style={{
                              marginLeft: '0.5rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              fontSize: '0.75rem',
                              borderRadius: '4px',
                              fontWeight: 500
                            }}>
                              CURRENT
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          Bid ID: {version.id}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {version.pdfGenerateDate && (
                        <div><strong>PDF Generated:</strong> {formatDate(version.pdfGenerateDate)}</div>
                      )}
                      {version.lastModified && (
                        <div><strong>Last Modified:</strong> {formatDate(version.lastModified)}</div>
                      )}
                      {version.filePath && (
                        <div><strong>File:</strong> {version.filePath.split('/').pop()}</div>
                      )}
                    </div>
                    {!version.archivedFlag && version.id !== bid.id && (
                      <button
                        onClick={() => navigate(`/bids/${version.id}`)}
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        View This Version
                      </button>
                    )}
                    {version.archivedFlag && (
                      <button
                        onClick={() => navigate(`/bids/${version.id}`)}
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        View Archived Version
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label>Internal Notes</label>
          <textarea
            value={bid.internalNotes || ''}
            readOnly
            disabled
            rows={4}
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleBack}
            className="cancel-button"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewBid;
